import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import {
  Headset,
  UserCircle,
  ChevronDown,
  Voicemail,
  LogOut,
  KeyRound,
  MoreHorizontal,
  Settings,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import type { User } from '../../providers/AuthProvider';
import { useAuth } from '../../providers/AuthProvider';
import { useWebSocket } from '../../providers/WebSocketProvider';
import { apiGet, apiPost } from '../../hooks/useApi';
import { OrgSwitcher } from './OrgSwitcher';
import { UserSwitcher } from './UserSwitcher';
import { FontSizeToggle } from './FontSizeToggle';
import { ThemeToggle } from './ThemeToggle';
import { LegacyToggle } from './LegacyToggle';
import { ActiveCallWidget } from './ActiveCallWidget';
import { PhoneDialer } from './PhoneDialer';
import { SoftphoneStatusIndicator } from './SoftphoneStatusIndicator';
import { NotificationBell } from './NotificationBell';
import { StatusClock } from './StatusClock';
import { WelcomeCallsCounter } from './WelcomeCallsCounter';
import { CallHistoryPanel } from '../voice/CallHistoryPanel';
import { VoicemailPanel } from '../voice/VoicemailPanel';
import { SupervisorMonitor } from '../voice/SupervisorMonitor';
import { TeamDrawer } from '../shared/TeamDrawer';

interface HeaderProps {
  user: User | null;
}

// ── Status config ────────────────────────────────────────────────────────

/**
 * Server-driven reason list (#496 Phase C.2). Reasons back the `not_ready`
 * state; admins manage them at `/api/admin/rep-status-reasons`. The picker
 * fetches active rows from `/api/kpi/status-reasons` and renders them
 * grouped by `category`. `Ready` itself is a state, not a reason, so it's
 * rendered above the reason list as a fixed item.
 */
interface StatusReason {
  code: string;
  label: string;
  category: 'productive' | 'away' | null;
  /** Marks the reason behind the leader Available/Not Available toggle (#1410). */
  leader_presence: boolean;
  sort_order: number;
}

/**
 * Per-code color map for the default reason set + the built-in states.
 * Preserves the visual language users are already tuned to, without
 * forcing the backend to carry presentation data. Custom admin-added
 * reasons fall back to a category-based color.
 */
const KNOWN_CODE_COLORS: Record<string, string> = {
  ready: 'bg-emerald-500',
  on_call: 'bg-cyan-500',
  wrap_up: 'bg-purple-400',
  talking: 'bg-blue-500',
  supervisor_help: 'bg-purple-500',
  training: 'bg-indigo-500',
  special_projects: 'bg-amber-500',
  meeting: 'bg-slate-500',
  break: 'bg-amber-400',
  personal: 'bg-amber-400',
  lunch: 'bg-red-500',
  pc_it_issues: 'bg-orange-500',
};

const SYSTEM_LABELS: Record<string, string> = {
  ready: 'Ready',
  on_call: 'On Call',
  wrap_up: 'Wrap-up',
};

function colorForReason(code: string, category: StatusReason['category']): string {
  const known = KNOWN_CODE_COLORS[code];
  if (known) return known;
  if (category === 'productive') return 'bg-emerald-500';
  if (category === 'away') return 'bg-amber-400';
  return 'bg-slate-500';
}

/** Resolve a status value's color — checks the system map, then the fetched reason list, then falls back. */
function getStatusColor(status: string, reasons: StatusReason[]): string {
  const known = KNOWN_CODE_COLORS[status];
  if (known) return known;
  const reason = reasons.find((r) => r.code === status);
  if (reason) return colorForReason(reason.code, reason.category);
  return 'bg-slate-500';
}

/** Resolve a status value's display label — system state, then reason list, then humanize the code. */
function formatLabel(status: string, reasons: StatusReason[]): string {
  const systemLabel = SYSTEM_LABELS[status];
  if (systemLabel) return systemLabel;
  const reason = reasons.find((r) => r.code === status);
  if (reason) return reason.label;
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Component ────────────────────────────────────────────────────────────

export function Header({ user }: HeaderProps) {
  const { logout } = useAuth();
  const { subscribe: subscribeWs, connected: wsConnected } = useWebSocket();

  const [currentStatus, setCurrentStatus] = useState('ready');
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [statusStartedAt, setStatusStartedAt] = useState<string | null>(null);
  const [productiveMinutes, setProductiveMinutes] = useState(0);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [clockBusy, setClockBusy] = useState(false);
  const [reasons, setReasons] = useState<StatusReason[]>([]);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // User menu state
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const [callHistoryOpen, setCallHistoryOpen] = useState(false);
  const [voicemailOpen, setVoicemailOpen] = useState(false);
  const [teamDrawerOpen, setTeamDrawerOpen] = useState(false);
  const [voicemailCount, setVoicemailCount] = useState(0);

  // Priority overflow: when the full control set is wider than the header,
  // the secondary controls (call history, voicemail, team, supervisor
  // monitor, accessibility + platform-ops toggles) collapse into a "More"
  // popover so the operational controls (status, clock, dialer,
  // notifications, impersonation, user menu) never get pushed off-screen.
  // Measured with a ResizeObserver instead of a media query (#1439) because
  // rendered width varies by role — platform-ops sees extra toggles — and by
  // call state (ActiveCallWidget mounts mid-call). Conditional render (not
  // CSS hiding) keeps each control single-mounted.
  const [compact, setCompact] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const rightControlsRef = useRef<HTMLDivElement>(null);
  // Mirror of `compact` for the ResizeObserver callback, which is created
  // once and would otherwise close over the initial value.
  const compactRef = useRef(compact);
  // Geometry signature (`clientWidth:controlsWidth`) at the last probe, so
  // the re-collapse a failed probe causes doesn't schedule another probe.
  const probeKeyRef = useRef('');
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // Collapse validation — runs on every commit while expanded, BEFORE paint,
  // so an overflowing full set never flashes (including first mount in a
  // small window). This is also what resolves a probe from the observer
  // below: the probe expands optimistically, and this effect re-collapses
  // in the same frame if the full set still overflows.
  useLayoutEffect(() => {
    compactRef.current = compact;
    if (compact) return;
    const header = headerRef.current;
    if (header && header.scrollWidth > header.clientWidth) setCompact(true);
  });

  useLayoutEffect(() => {
    const header = headerRef.current;
    const controls = rightControlsRef.current;
    if (!header || !controls) return;
    const check = () => {
      if (!compactRef.current) {
        // Controls are start-aligned, so overflow spills past the END
        // (right) edge — the one edge scrollWidth measures. Keep the header
        // start-aligned: under `justify-end` the spill moves to the start
        // edge, which scrollWidth never counts, and detection goes blind
        // (the #1463 regression).
        if (header.scrollWidth > header.clientWidth) setCompact(true);
        return;
      }
      // Compact renders only the reduced set, so the full set's current
      // width is unknowable without rendering it — and it changes as
      // route-scoped controls (queue pills, call widget) mount and unmount.
      // Comparing against a width remembered at collapse time goes stale
      // the moment that content shrinks, leaving the bar stuck compact
      // beside empty space. Probe instead: expand and let the validation
      // effect above re-collapse before paint if it still overflows.
      const key = `${header.clientWidth}:${controls.scrollWidth}`;
      if (key === probeKeyRef.current) return;
      probeKeyRef.current = key;
      setCompact(false);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(header);
    // The right-side container resizes when its content changes (call widget
    // mounts, role-gated toggles load) without the header box changing —
    // observing it re-runs the check on those.
    ro.observe(controls);
    return () => ro.disconnect();
  }, []);

  // Close the More popover on outside click. Child dropdowns (e.g. the org
  // switcher menu) live inside moreRef's subtree, so they don't trigger it.
  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [moreOpen]);

  // Reconcile local clock/status state from /api/kpi/session-status — the
  // single authoritative read of Redis live state. Returns the post-fetch
  // active flag and current status (or `null` if the fetch itself failed)
  // so callers can decide whether to suppress an error toast when the
  // server outcome already matches what the user wanted.
  const reconcileSessionStatus = useCallback(async (): Promise<{
    active: boolean | null;
    status: string | null;
  }> => {
    try {
      const data = await apiGet<{
        active_session: boolean;
        session_id?: string;
        started_at?: string;
        current_status?: string;
        status_started_at?: string;
        productive_minutes?: number;
      }>('/api/kpi/session-status');
      if (data.active_session && data.started_at) {
        const status = data.current_status || 'ready';
        setIsClockedIn(true);
        setCurrentStatus(status);
        setStatusStartedAt(data.status_started_at || data.started_at);
        setProductiveMinutes(data.productive_minutes || 0);
        return { active: true, status };
      }
      setIsClockedIn(false);
      setStatusStartedAt(null);
      setProductiveMinutes(0);
      return { active: false, status: null };
    } catch {
      return { active: null, status: null };
    }
  }, []);

  // Initial load on mount.
  useEffect(() => {
    void reconcileSessionStatus();
  }, [reconcileSessionStatus]);

  // Catch up on any server-pushed state changes missed during a WS
  // disconnect. The session_ended / rep_status_changed pubsub is
  // fire-and-forget — a tab offline at publish time would otherwise stay
  // stale until a hard refresh. Re-fetching on every reconnect is cheap
  // and idempotent.
  useEffect(() => {
    if (wsConnected) void reconcileSessionStatus();
  }, [wsConnected, reconcileSessionStatus]);

  // Catch up after the tab returns from background or sleep — the WS may
  // have stayed connected but events arriving while the tab was inactive
  // can still leave React state behind the server.
  useEffect(() => {
    const handler = (): void => {
      if (document.visibilityState === 'visible') void reconcileSessionStatus();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [reconcileSessionStatus]);

  // Load tenant's reason catalog for the picker (#496 Phase C.2). Admins
  // manage the list via /api/admin/rep-status-reasons; every rep needs it
  // to render their dropdown.
  useEffect(() => {
    apiGet<{ reasons: StatusReason[] }>('/api/kpi/status-reasons')
      .then((data) => setReasons(data.reasons ?? []))
      .catch(() => setReasons([]));
  }, []);

  // React to server-driven status changes (Phase A voice auto-transitions,
  // Phase A.5 wrap-up expiry, #496 supervisor force-change). Without this
  // subscription the picker and StatusClock stay on the old status until
  // the next session-status fetch. `user.sub` is the apex-queue users.id
  // (see AuthProvider load) — same value the server stamps as `user_id`
  // in the rep_status_changed payload. When the event carries
  // `forced_by_name` the transition was a supervisor force; we surface
  // a toast so the rep isn't surprised by the state flip.
  useEffect(() => {
    if (!user?.sub) return;
    // The WS hub delivers `{ type, channel, data: {...} }`; the payload we
    // care about is on `.data`. Reading the fields off `raw` directly was
    // the original shape bug — `ev.user_id` was always undefined and the
    // handler silently early-returned, so the rep's own status never
    // updated from a supervisor force or wrap-up expiry until the next
    // session-status reconcile.
    const unsub = subscribeWs('rep_status_changed', (raw) => {
      const ev =
        (raw as { data?: Record<string, unknown> })?.data ?? ({} as Record<string, unknown>);
      const userId = ev.user_id as string | undefined;
      const status = ev.status as string | undefined;
      const statusChangedAt = ev.status_changed_at as number | undefined;
      const forcedById = ev.forced_by_id as string | undefined;
      const forcedByName = ev.forced_by_name as string | undefined;
      const forcedNote = ev.forced_note as string | null | undefined;
      if (!userId || userId !== user.sub) return;
      if (!status) return;
      setCurrentStatus(status);
      setStatusStartedAt(
        statusChangedAt ? new Date(statusChangedAt).toISOString() : new Date().toISOString(),
      );
      // #496 force-change toast. Skip when the forcer is the rep themselves
      // (supervisor forcing their own state) — they already know.
      if (forcedByName && forcedById && forcedById !== user.sub) {
        const statusLabel = status.replace(/_/g, ' ');
        toast.info(`${forcedByName} set your status to ${statusLabel}`, {
          description: forcedNote ?? undefined,
          duration: 6000,
        });
      }
    });
    return unsub;
  }, [subscribeWs, user?.sub]);

  // React to server-driven session_ended events. Two callers fire it:
  // the rep's own clock-out (in another tab — keeps tabs in sync) and a
  // supervisor force-clock-out via /api/admin/rep-status/:userId/clock-out.
  // The forced_by_* fields are present only on the supervisor path; we
  // toast in that case so the kicked rep sees who did it.
  useEffect(() => {
    if (!user?.sub) return;
    const unsub = subscribeWs('session_ended', (raw) => {
      const ev =
        (raw as { data?: Record<string, unknown> })?.data ?? ({} as Record<string, unknown>);
      const userId = ev.user_id as string | undefined;
      const forcedById = ev.forced_by_id as string | undefined;
      const forcedByName = ev.forced_by_name as string | undefined;
      const forcedNote = ev.forced_note as string | null | undefined;
      if (!userId || userId !== user.sub) return;
      setIsClockedIn(false);
      setStatusStartedAt(null);
      if (forcedByName && forcedById && forcedById !== user.sub) {
        toast.info(`${forcedByName} clocked you out`, {
          description: forcedNote ?? undefined,
          duration: 6000,
        });
      }
    });
    return unsub;
  }, [subscribeWs, user?.sub]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClockIn = useCallback(
    async (initialStatus?: string) => {
      if (clockBusy) return;
      setClockBusy(true);
      try {
        const data = await apiPost<{
          session_id: string;
          started_at: string;
          status: string;
        }>('/api/kpi/clock-in', initialStatus ? { status: initialStatus } : {});
        setIsClockedIn(true);
        setStatusStartedAt(new Date().toISOString());
        setProductiveMinutes(0);
        setCurrentStatus(data.status || 'ready');
      } catch (err) {
        // Reconcile against the server's live state. If it says we're
        // already clocked in (another tab, race), the user got what they
        // wanted — no toast.
        const result = await reconcileSessionStatus();
        if (result.active !== true) {
          console.error('[Header] clock-in failed:', err);
          toast.error(err instanceof Error ? err.message : 'Failed to clock in');
        }
      } finally {
        setClockBusy(false);
      }
    },
    [clockBusy, reconcileSessionStatus],
  );

  const handleClockOut = useCallback(async () => {
    if (clockBusy) return;
    setClockBusy(true);
    try {
      await apiPost('/api/kpi/clock-out', {});
      setIsClockedIn(false);
      setStatusStartedAt(null);
      setProductiveMinutes(0);
      setCurrentStatus('ready');
    } catch (err) {
      // Server is the source of truth on live clocked-in state. A 4xx
      // here typically means the session was already closed — supervisor
      // force, auto-timeout, another tab — in which case the user's
      // clock-out intent is already satisfied. Reconcile and only toast
      // if the server still says we're clocked in (issue #701).
      const result = await reconcileSessionStatus();
      if (result.active !== false) {
        console.error('[Header] clock-out failed:', err);
        toast.error(err instanceof Error ? err.message : 'Failed to clock out');
      }
    } finally {
      setClockBusy(false);
    }
  }, [clockBusy, reconcileSessionStatus]);

  const handleStatusChange = useCallback(
    async (status: string) => {
      setStatusDropdownOpen(false);
      if (!isClockedIn) return;

      // Accumulate productive time from the status we're leaving
      const productiveStatuses = [
        'ready',
        'talking',
        'supervisor_help',
        'special_projects',
        'meeting',
      ];
      if (statusStartedAt && productiveStatuses.includes(currentStatus)) {
        const elapsed = Math.floor((Date.now() - new Date(statusStartedAt).getTime()) / 60000);
        setProductiveMinutes((prev) => prev + elapsed);
      }

      // Reset timer immediately (optimistic — don't wait for API)
      const nowIso = new Date().toISOString();
      setCurrentStatus(status);
      setStatusStartedAt(nowIso);

      // Then persist to server. On rejection (typically: not clocked in,
      // or admin deactivated the reason), snap back to whatever the
      // server says is real — and surface the error unless the
      // reconciled status happens to match what the user picked.
      try {
        await apiPost('/api/kpi/status', { status });
      } catch (err) {
        const result = await reconcileSessionStatus();
        if (result.status !== status) {
          console.error('[Header] status change failed:', err);
          toast.error(err instanceof Error ? err.message : 'Failed to change status');
        }
      }
    },
    [isClockedIn, statusStartedAt, currentStatus, reconcileSessionStatus],
  );

  return (
    <header
      ref={headerRef}
      className="h-16 border-b border-border/60 flex items-center justify-start bg-card px-6 z-50 shadow-[0_2px_8px_rgba(0,0,0,0.02)] flex-shrink-0 relative"
    >
      {/* Right: status + clock + phone + notifications + user */}
      <div ref={rightControlsRef} className="flex items-center gap-2 xl:gap-4">
        <div className="flex items-center gap-2">
          {/* Status dropdown — locked while on an active call (system-driven
              state; rep shouldn't change status mid-call). Stays enabled
              during wrap_up so the rep can exit wrap-up by picking the next
              status. */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() =>
                isClockedIn &&
                currentStatus !== 'on_call' &&
                setStatusDropdownOpen(!statusDropdownOpen)
              }
              disabled={!isClockedIn || currentStatus === 'on_call'}
              title={currentStatus === 'on_call' ? 'Status locked while on a call' : undefined}
              className={`flex items-center gap-2 bg-secondary/30 hover:bg-secondary/60 border border-border/60 px-3 py-1.5 rounded-lg transition-colors shadow-sm min-w-[110px] justify-between ${!isClockedIn || currentStatus === 'on_call' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full ${getStatusColor(currentStatus, reasons)} shadow-sm`}
                />
                <span className="text-[0.75rem] font-medium text-foreground">
                  {formatLabel(currentStatus, reasons)}
                </span>
              </div>
              <ChevronDown
                className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {statusDropdownOpen && (
              <div className="absolute top-full left-0 mt-1.5 w-[180px] bg-card border border-border/80 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] z-50 overflow-hidden">
                <div className="py-1.5">
                  {/* Ready is a state, not a reason — rendered as a fixed top item. */}
                  <button
                    onClick={() => handleStatusChange('ready')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-[0.75rem] hover:bg-secondary transition-colors cursor-pointer ${currentStatus === 'ready' ? 'bg-primary/5 font-medium' : ''}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${KNOWN_CODE_COLORS.ready}`} />
                    Ready
                  </button>

                  {(() => {
                    const productive = reasons.filter((r) => r.category === 'productive');
                    const away = reasons.filter((r) => r.category === 'away');
                    const other = reasons.filter(
                      (r) => r.category !== 'productive' && r.category !== 'away',
                    );
                    return (
                      <>
                        {productive.length > 0 && (
                          <>
                            <div className="h-px w-full bg-border/40 my-1" />
                            <div className="px-3 py-1 text-[0.625rem] font-bold text-muted-foreground uppercase tracking-wider">
                              Productive
                            </div>
                            {productive.map((r) => (
                              <ReasonItem
                                key={r.code}
                                reason={r}
                                selected={currentStatus === r.code}
                                onSelect={handleStatusChange}
                              />
                            ))}
                          </>
                        )}
                        {away.length > 0 && (
                          <>
                            <div className="h-px w-full bg-border/40 my-1" />
                            <div className="px-3 py-1 text-[0.625rem] font-bold text-muted-foreground uppercase tracking-wider">
                              Away
                            </div>
                            {away.map((r) => (
                              <ReasonItem
                                key={r.code}
                                reason={r}
                                selected={currentStatus === r.code}
                                onSelect={handleStatusChange}
                              />
                            ))}
                          </>
                        )}
                        {other.length > 0 && (
                          <>
                            <div className="h-px w-full bg-border/40 my-1" />
                            <div className="px-3 py-1 text-[0.625rem] font-bold text-muted-foreground uppercase tracking-wider">
                              Other
                            </div>
                            {other.map((r) => (
                              <ReasonItem
                                key={r.code}
                                reason={r}
                                selected={currentStatus === r.code}
                                onSelect={handleStatusChange}
                              />
                            ))}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Clock in/out. Leader-presence users (#1410) get an
              Available / Not Available toggle instead: same session
              machinery, but clock-in lands directly in the flagged
              reason so they are never routable in a `ready` gap. The
              picker endpoint already role-filters, so the flagged
              reason's presence in `reasons` IS the eligibility check. */}
          {(() => {
            const leaderReason = reasons.find((r) => r.leader_presence);
            if (leaderReason) {
              return (
                <button
                  onClick={() =>
                    void (isClockedIn ? handleClockOut() : handleClockIn(leaderReason.code))
                  }
                  disabled={clockBusy}
                  className={`px-4 py-1.5 rounded-lg text-[0.75rem] font-semibold transition-all shadow-sm border cursor-pointer disabled:opacity-50 ${
                    isClockedIn
                      ? 'bg-red-500 hover:bg-red-600 text-white border-red-600/50'
                      : 'bg-brand-green hover:bg-brand-green/90 text-white border-brand-green/80'
                  }`}
                >
                  {clockBusy ? '...' : isClockedIn ? 'Not Available' : 'Available'}
                </button>
              );
            }
            return (
              <button
                onClick={() => void (isClockedIn ? handleClockOut() : handleClockIn())}
                disabled={clockBusy}
                className={`px-4 py-1.5 rounded-lg text-[0.75rem] font-semibold transition-all shadow-sm border cursor-pointer disabled:opacity-50 ${
                  isClockedIn
                    ? 'bg-red-500 hover:bg-red-600 text-white border-red-600/50'
                    : 'bg-brand-green hover:bg-brand-green/90 text-white border-brand-green/80'
                }`}
              >
                {clockBusy ? '...' : isClockedIn ? 'Clock Out' : 'Clock In'}
              </button>
            );
          })()}

          {/* Welcome Calls badge (queue pages) + Team pill (all pages). The
              Team pill is the entry point to the presence drawer — see #1469. */}
          <WelcomeCallsCounter onOpenTeam={() => setTeamDrawerOpen(true)} />

          {/* Status timer + productive accumulator */}
          <StatusClock
            statusLabel={formatLabel(currentStatus, reasons)}
            statusStartedAt={statusStartedAt}
            isClockedIn={isClockedIn}
            productiveMinutes={productiveMinutes}
          />
        </div>

        <div className="h-5 w-px bg-border/60 mx-1" />

        {/* Phone controls */}
        <div className="flex items-center gap-1">
          {!compact && (
            <button
              onClick={() => setCallHistoryOpen(true)}
              className="w-8 h-8 rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground flex items-center justify-center transition-colors cursor-pointer"
              title="Call history"
            >
              <Headset className="w-4 h-4 stroke-[1.5]" />
            </button>
          )}
          <PhoneDialer />
          <SoftphoneStatusIndicator />
        </div>

        <div className="w-px h-5 bg-border/60 mx-1" />

        {/* Notifications */}
        <NotificationBell />

        {!compact && (
          <>
            {/* Voicemail */}
            <button
              onClick={() => setVoicemailOpen(true)}
              className="p-2 hover:bg-secondary transition-colors relative text-muted-foreground hover:text-foreground rounded-full border border-transparent cursor-pointer"
              title="Voicemail"
            >
              <Voicemail className="w-4 h-4 stroke-[1.5]" />
              {voicemailCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-destructive text-white text-[0.5625rem] font-bold flex items-center justify-center border-2 border-card">
                  {voicemailCount > 9 ? '9+' : voicemailCount}
                </span>
              )}
            </button>

            {/* Supervisor monitor — self-gates to supervisor+ roles (renders null
                otherwise). Live listen / whisper / barge into any active call. */}
            <SupervisorMonitor />
          </>
        )}

        {/* Active call widget */}
        <ActiveCallWidget />

        {!compact && (
          <>
            {/* Accessibility — visible to all users */}
            <FontSizeToggle />

            {/* Platform-ops tools + Org switcher */}
            <ThemeToggle />
            <LegacyToggle />
            <OrgSwitcher />
          </>
        )}

        {/* Impersonation stays out of the overflow menu at every width —
            it's the switch-back control while viewing as another role.
            Renders null for non-platform-ops. */}
        <UserSwitcher />

        {compact && (
          <div className="relative" ref={moreRef}>
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className="p-2 hover:bg-secondary transition-colors relative text-muted-foreground hover:text-foreground rounded-full border border-transparent cursor-pointer"
              title="More tools"
            >
              <MoreHorizontal className="w-4 h-4 stroke-[1.5]" />
              {/* Unread-voicemail dot — the count itself lives on the
                  voicemail button inside the popover. */}
              {voicemailCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-destructive border-2 border-card" />
              )}
            </button>

            {moreOpen && (
              <div className="absolute top-full right-0 mt-1.5 bg-card border border-border/80 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] z-50 p-1.5 flex items-center gap-1">
                <button
                  onClick={() => setCallHistoryOpen(true)}
                  className="w-8 h-8 rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground flex items-center justify-center transition-colors cursor-pointer"
                  title="Call history"
                >
                  <Headset className="w-4 h-4 stroke-[1.5]" />
                </button>
                <button
                  onClick={() => setVoicemailOpen(true)}
                  className="p-2 hover:bg-secondary transition-colors relative text-muted-foreground hover:text-foreground rounded-full border border-transparent cursor-pointer"
                  title="Voicemail"
                >
                  <Voicemail className="w-4 h-4 stroke-[1.5]" />
                  {voicemailCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-destructive text-white text-[0.5625rem] font-bold flex items-center justify-center border-2 border-card">
                      {voicemailCount > 9 ? '9+' : voicemailCount}
                    </span>
                  )}
                </button>
                <SupervisorMonitor />
                <FontSizeToggle />
                <ThemeToggle />
                <LegacyToggle />
                <OrgSwitcher />
              </div>
            )}
          </div>
        )}

        <div className="h-5 w-px bg-border/60" />

        {/* User menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className={`flex items-center gap-2 px-2.5 py-1 transition-colors rounded-full border cursor-pointer ${userMenuOpen ? 'bg-secondary border-border/60' : 'hover:bg-secondary border-transparent'}`}
          >
            <UserCircle className="w-6 h-6 text-slate-400 stroke-[1.5]" />
            <div className="text-left hidden md:block">
              <p className="text-[0.75rem] font-medium leading-none text-foreground">
                {user?.name || 'User'}
              </p>
              <p className="text-[0.625rem] text-muted-foreground leading-none mt-1 capitalize">
                {user?.role || 'rep'}
              </p>
            </div>
            <ChevronDown
              className={`w-3 h-3 text-muted-foreground transition-transform hidden md:block ${userMenuOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {userMenuOpen && (
            <div className="absolute top-full right-0 mt-1.5 w-[200px] bg-card border border-border/80 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] z-50 overflow-hidden">
              {/* User info header */}
              <div className="px-4 py-3 border-b border-border/40">
                <p className="text-[0.8125rem] font-semibold text-foreground truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-[0.6875rem] text-muted-foreground truncate mt-0.5">
                  {user?.email || ''}
                </p>
                {user?.activeOrgName && (
                  <p className="text-[0.625rem] text-muted-foreground/70 truncate mt-1">
                    {user.activeOrgName}
                  </p>
                )}
              </div>

              <div className="py-1">
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/dashboard');
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-[0.75rem] text-foreground hover:bg-secondary transition-colors cursor-pointer"
                >
                  <Settings className="w-3.5 h-3.5 text-muted-foreground stroke-[1.5]" />
                  My KPIs
                </button>

                <div className="h-px w-full bg-border/40 my-0.5" />

                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    void logout();
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-[0.75rem] text-destructive hover:bg-destructive/5 transition-colors cursor-pointer"
                >
                  <LogOut className="w-3.5 h-3.5 stroke-[1.5]" />
                  Log out
                </button>

                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    void logout({ forgetLoginMethod: true });
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-[0.75rem] text-foreground hover:bg-secondary transition-colors cursor-pointer"
                >
                  <KeyRound className="w-3.5 h-3.5 text-muted-foreground stroke-[1.5]" />
                  Change sign-in method
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <TeamDrawer open={teamDrawerOpen} onClose={() => setTeamDrawerOpen(false)} />
      <CallHistoryPanel open={callHistoryOpen} onClose={() => setCallHistoryOpen(false)} />
      <VoicemailPanel
        open={voicemailOpen}
        onClose={() => setVoicemailOpen(false)}
        onUnreadCountChange={setVoicemailCount}
      />
    </header>
  );
}

function ReasonItem({
  reason,
  selected,
  onSelect,
}: {
  reason: StatusReason;
  selected: boolean;
  onSelect: (code: string) => void;
}) {
  return (
    <button
      onClick={() => onSelect(reason.code)}
      className={`w-full flex items-center gap-2 px-3 py-1.5 text-[0.75rem] hover:bg-secondary transition-colors cursor-pointer ${selected ? 'bg-primary/5 font-medium' : ''}`}
    >
      <span className={`w-2 h-2 rounded-full ${colorForReason(reason.code, reason.category)}`} />
      {reason.label}
    </button>
  );
}
